import React from "react";
import api from "../api";

const POLL_INTERVAL = 3000;

export default function usePollBatchStatus() {
  const [logs, setLogs] = React.useState([]);
  const [isPolling, setIsPolling] = React.useState(false);
  const [isComplete, setIsComplete] = React.useState(false);
  const [error, setError] = React.useState(null);
  const intervalRef = React.useRef(null);
  const logIdsRef = React.useRef([]);

  const summary = React.useMemo(() => {
    return logs.reduce(
      (acc, log) => {
        switch (log.status) {
          case "done":
            acc.done++;
            acc.completed++;
            break;
          case "failed":
            acc.failed++;
            acc.completed++;
            break;
          case "finished_with_errors":
            acc.withErrors++;
            acc.completed++;
            break;
          case "in_progress":
            acc.inProgress++;
            break;
          default:
            acc.pending++;
        }
        return acc;
      },
      {
        total: logs.length,
        done: 0,
        failed: 0,
        withErrors: 0,
        inProgress: 0,
        pending: 0,
        completed: 0
      }
    );
  }, [logs]);

  const stopPolling = React.useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const fetchStatus = React.useCallback(async () => {
    if (logIdsRef.current.length === 0) return;

    try {
      const response = await api.getPmsRequestLogs({
        ids: logIdsRef.current.join(","),
        per_page: logIdsRef.current.length
      });

      // Handle different response formats from the API
      let fetchedLogs = [];
      if (response.data)
        if (Array.isArray(response.data)) fetchedLogs = response.data;
        else if (response.data.items) fetchedLogs = response.data.items;
        else if (response.data.data) fetchedLogs = response.data.data;

      // Sort logs to match the original order of IDs
      const idOrder = logIdsRef.current.reduce((acc, id, idx) => {
        acc[id] = idx;
        return acc;
      }, {});
      fetchedLogs.sort(
        (a, b) => (idOrder[a.id] ?? 999) - (idOrder[b.id] ?? 999)
      );

      setLogs(fetchedLogs);

      // Check if all logs are in a terminal state
      const allComplete =
        fetchedLogs.length > 0 &&
        fetchedLogs.every((log) =>
          ["done", "failed", "finished_with_errors"].includes(log.status)
        );

      if (allComplete) {
        stopPolling();
        setIsComplete(true);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch batch status");
      stopPolling();
    }
  }, [stopPolling]);

  const startPolling = React.useCallback(
    (logIds) => {
      if (!logIds || logIds.length === 0) return;

      // Clear any existing interval
      if (intervalRef.current) clearInterval(intervalRef.current);

      logIdsRef.current = logIds;
      setLogs([]);
      setIsPolling(true);
      setIsComplete(false);
      setError(null);

      // Fetch immediately
      fetchStatus();

      // Set up interval for subsequent fetches
      intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL);
    },
    [fetchStatus]
  );

  const reset = React.useCallback(() => {
    stopPolling();
    logIdsRef.current = [];
    setLogs([]);
    setIsComplete(false);
    setError(null);
  }, [stopPolling]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return {
    logs,
    isPolling,
    isComplete,
    error,
    summary,
    startPolling,
    reset
  };
}
